import React, { useEffect, useState } from 'react';
import {
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TextField,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Stack,
  Alert,
  CircularProgress,
  InputAdornment,
  IconButton,
  Tooltip,
  Typography,
} from '@mui/material';

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import { AcceptRejectDialog } from '../CommonFunctions/DialogComponents';

import styles from './ssoconfigurationform.module.css';

import {
  getSSODetails,
  saveSSOConfig,
  updateSSOConfig,
  resetSSOVerification,
  enableSSO,
  disableSSO,
  getScimToken,
  ISSOFormData,
} from '../../api/ssoApi';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';

// --- TYPES ---
interface IFormData {
  provider: string;
  discoveryUrl: string;
  clientId: string;
  clientSecret: string;
}

interface IScimData {
  url: string;
  token: string;
}

interface IFormErrors {
  discoveryUrl: boolean;
  clientId: boolean;
  clientSecret: boolean;
}

const panelTooltip = 'Complete the previous step to open this section';

const AccordionWithTooltip = ({
  disabled,
  tooltip,
  children,
}: {
  disabled: boolean;
  tooltip: string;
  children: React.ReactNode;
}) => {
  if (!disabled) return <>{children}</>;

  return (
    <Tooltip title={tooltip} placement="top" arrow followCursor>
      <Box>{children}</Box>
    </Tooltip>
  );
};

const SSOConfiguration = () => {
  const [isSSOEnabled, setIsSSOEnabled] = useState<boolean>(false);
  const [expanded, setExpanded] = useState<string | false>('panel1');
  const [loading, setLoading] = useState<boolean>(false);
  const [alertMsg, setAlertMsg] = useState<{
    type: 'success' | 'error' | 'info';
    text: string;
  } | null>(null);

  // Logic: 0 = Not Started, 1 = Config Saved, 2 = Verified, 3 = Activated
  const [progressStep, setProgressStep] = useState<number>(0);
  const [isFormLocked, setIsFormLocked] = useState<boolean>(false);
  const [hasExistingConfig, setHasExistingConfig] = useState<boolean>(false);
  const [showUpdateConfirm, setShowUpdateConfirm] = useState<boolean>(false);

  const [formData, setFormData] = useState<IFormData>({
    provider: 'entra',
    discoveryUrl: '',
    clientId: '',
    clientSecret: '',
  });

  const [savedFormData, setSavedFormData] = useState<IFormData | null>(null);

  const [isVerified, setIsVerified] = useState(false);

  const [errors, setErrors] = useState<IFormErrors>({
    discoveryUrl: false,
    clientId: false,
    clientSecret: false,
  });

  const [scimData, setScimData] = useState<IScimData>({ url: '', token: '' });
  const [copiedField, setCopiedField] = useState<'url' | 'token' | null>(null);

  const userRole = sessionStorage.getItem('access');

  const email = useSelector((state: RootState) => state.isLogged.email);

  const userEmail = encodeURIComponent(email);

  useEffect(() => {
    const fetchDetails = async () => {
      setLoading(true);
      const response = await getSSODetails();
      setLoading(false);

      if (!response.isError && response.data) {
        const data = response.data;

        // If we have an IDP type, it means config exists
        if (data.idpType || data.provider || data.discoveryUrl) {
          const loadedData = {
            provider: data.idpType || 'entra',
            discoveryUrl: data.discoveryUrl || '',
            clientId: data.clientId || '',
            clientSecret: data.clientSecret || '',
          };

          setAlertMsg({
            type: 'success',
            text: 'SSO is Configured. You can now verify and activate SSO.',
          });

          setHasExistingConfig(true);
          setFormData(loadedData);
          setSavedFormData(loadedData);

          const enabled = data.sso === 'enabled';
          const Verifiedflag =
            data.verified || new URLSearchParams(window.location.search).get('verify') === 'true';

          const verifyError = new URLSearchParams(window.location.search).get('error');

          setIsVerified(Verifiedflag);

          // Restore Progress State
          let step = 1;

          if (isVerified || Verifiedflag) step = 2;
          if (enabled) step = 3;

          setIsSSOEnabled(enabled);

          setProgressStep(step);
          // Lock form if configuration exists
          setIsFormLocked(true);

          if (step === 1) setExpanded('panel2');
          else if (step === 2) setExpanded('panel3');
          else if (step === 3) setExpanded('panel4');

          if (Verifiedflag) {
            setAlertMsg({
              type: 'success',
              text: 'Configuration verified successfully. You can proceed to activate SSO.',
            });
            if (data.sso === 'disabled') {
              setAlertMsg({
                type: 'info',
                text: 'SSO is disabled. Click on Enable button to use SSO.',
              });
            }
          }
          if (!Verifiedflag && verifyError) {
            setAlertMsg({
              type: 'error',
              text:
                verifyError ||
                'Failed to verify the configuration. Please check your details and try again.',
            });
          }
          if (enabled) {
            setAlertMsg({
              type: 'success',
              text: 'SSO activation is done. You can generate new token or use existing one.',
            });
          }
        }
      }
    };
    if (userRole === 'account_admin') {
      fetchDetails();
    }
  }, []);

  // Handle Accordion Change
  const handleChange = (panel: string) => (event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  const handleInputChange =
    (field: keyof IFormData) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      setErrors({ ...errors, [field]: false });
      setFormData({ ...formData, [field]: event.target.value });
    };

  //VALIDATION LOGIC
  const validateForm = (): boolean => {
    const newErrors = {
      discoveryUrl: formData.discoveryUrl.trim() === '',
      clientId: formData.clientId.trim() === '',
      clientSecret: formData.clientSecret.trim() === '',
    };
    setErrors(newErrors);
    return !Object.values(newErrors).some((isError) => isError);
  };

  //Reset Handler
  const handleBack = () => {
    if (savedFormData) {
      // Restore the data
      setFormData(savedFormData);
      // Re-lock the form as the user is cancelling their edit
      setIsFormLocked(true);

      //  Restore the visual progress
      //let prevStep = 1;
      if (isSSOEnabled) {
        setProgressStep(3);
        setExpanded('panel4');
      } else if (isVerified) {
        // verified = true, sso = null
        setProgressStep(2);
        setExpanded('panel3');
      } else if (hasExistingConfig) {
        // Config saved but not verified
        setProgressStep(1);
        setExpanded('panel2');
      } else {
        setProgressStep(0);
        setExpanded('panel1');
      }
    } else {
      // Reset to empty if no configuration was ever saved
      setFormData({ provider: 'entra', discoveryUrl: '', clientId: '', clientSecret: '' });
      setIsFormLocked(false);
      setProgressStep(0);
      setExpanded('panel1');
    }
    setErrors({ discoveryUrl: false, clientId: false, clientSecret: false });
  };

  const handleEditRequest = async () => {
    setIsFormLocked(false);
    setProgressStep(0);
    setExpanded('panel1');

    setFormData((prev) => ({
      ...prev,
      clientSecret: '',
    }));
    setAlertMsg({
      type: 'info',
      text: 'Configuration unlocked. You can now update the details.',
    });
  };

  // SAVE/UPDATE logic
  const proceedSaveOrUpdate = async () => {
    setLoading(true);
    setAlertMsg(null);

    const payload: ISSOFormData = {
      idpType: formData.provider,
      discoveryUrl: formData.discoveryUrl,
      clientId: formData.clientId,
      clientSecret: formData.clientSecret,
    };

    let response;
    if (hasExistingConfig) {
      await resetSSOVerification();
      response = await updateSSOConfig(payload);
    } else {
      response = await saveSSOConfig(payload);
    }

    setLoading(false);

    if (response.isError) {
      setAlertMsg({ type: 'error', text: response.message });
      return;
    }

    setHasExistingConfig(true);
    setIsFormLocked(true);
    setSavedFormData(formData);
    setProgressStep(1);
    setExpanded('panel2');

    setIsSSOEnabled(false);
    setIsVerified(false);

    setAlertMsg({
      type: 'success',
      text: hasExistingConfig
        ? 'Configuration updated. Please verify and activate SSO again.'
        : 'Configuration saved successfully.',
    });
  };

  // 1. SAVE CONFIGURATION
  const handleSaveConfig = async () => {
    if (!validateForm()) return;

    if (hasExistingConfig) {
      setShowUpdateConfirm(true);
      return;
    }

    proceedSaveOrUpdate();
  };

  // 2. VERIFY IDP
  const handleVerify = () => {
    const baseUrl = import.meta.env.VITE_SSO_VERIFY_BASE_URL;
    const verifyUrl = `${baseUrl}?m_id=${userEmail}`;
    window.location.href = verifyUrl;
  };

  // 3. ENABLE / DISABLE
  const handleToggleSSO = async (enable: boolean) => {
    setLoading(true);
    setAlertMsg(null);

    const response = enable ? await enableSSO() : await disableSSO();

    setLoading(false);

    if (response.isError) {
      setAlertMsg({ type: 'error', text: response.message });
      return;
    }

    setIsSSOEnabled(enable);

    setAlertMsg({
      type: 'success',
      text: response.message,
    });

    if (enable) {
      setProgressStep(3); // Fully activated
      setExpanded('panel4');
    } else {
      setProgressStep(2); // Fallback to Verified state (Panel 3 active)
      setExpanded('panel3');
    }
  };

  // 4. GENERATE TOKEN
  const handleGenerateToken = async () => {
    setLoading(true);
    setAlertMsg(null);

    const response = await getScimToken();
    setLoading(false);

    if (response.isError) {
      setAlertMsg({ type: 'error', text: response.message });
    } else {
      const resData = response.data;
      const tokenString =
        typeof resData === 'string'
          ? resData
          : resData.token || resData.scimToken || JSON.stringify(resData);

      setScimData({
        url: 'https://api.silzila.com/scim/v2',
        token: tokenString,
      });
    }
  };

  // Helper function to copy text
  const handleCopy = (text: string, field: 'url' | 'token') => {
    navigator.clipboard.writeText(text);
    setCopiedField(field);

    setTimeout(() => {
      setCopiedField(null);
    }, 1500);
  };

  return (
    <div className={styles.ssoContainer}>
      <AcceptRejectDialog
        open={showUpdateConfirm}
        heading="Update IDP Configuration?"
        messages={[
          {
            text: 'Updating IDP configuration will require re-verification.',
          },
          {
            text: 'You must verify and activate SSO again after updating.',
            style: { fontWeight: 500 },
          },
        ]}
        acceptText="Update"
        rejectText="Cancel"
        acceptFunction={() => {
          setShowUpdateConfirm(false);
          proceedSaveOrUpdate();
        }}
        rejectFunction={() => setShowUpdateConfirm(false)}
        closeFunction={() => setShowUpdateConfirm(false)}
      />
      <div className={styles.ssoHeader}>
        <h3 className={styles.ssoHeaderTitle}>
          {hasExistingConfig && !isFormLocked ? 'Edit SSO Configuration' : 'SSO Configuration'}
        </h3>
      </div>

      {userRole === 'account_admin' && (
        <div className={styles.ssoContentBody}>
          {alertMsg && (
            <Alert
              sx={{
                alignItems: 'center',
                '& .MuiAlert-icon': {
                  alignItems: 'center',
                },
                '& .MuiAlert-action': {
                  paddingTop: 0,
                  alignItems: 'center',
                },
              }}
              severity={alertMsg.type}
              className={styles.alert}
              onClose={() => setAlertMsg(null)}
            >
              {alertMsg.text}
            </Alert>
          )}

          {loading && (
            <Box display="flex" justifyContent="center" mb={2}>
              <CircularProgress size={24} />
            </Box>
          )}
          {/* --- PANEL 1: IDP CONFIGURATION --- */}
          <Accordion
            expanded={expanded === 'panel1'}
            onChange={handleChange('panel1')}
            // Lock: Cannot open if SSO is Disabled
            disabled={!isSSOEnabled && progressStep >= 2}
            disableGutters
            elevation={0}
            className={styles.accordionRoot}
          >
            <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
              <Stack
                direction="row"
                alignItems="center"
                width="100%"
                justifyContent="space-between"
              >
                <span className={styles.accordionTitle}>IDP Configuration</span>
                {progressStep >= 1 && (
                  <Tooltip title="IDP Configuration is done" arrow placement="top">
                    <CheckCircleIcon
                      color="success"
                      sx={{ fontSize: '18px', marginRight: '10px' }}
                    />
                  </Tooltip>
                )}
              </Stack>
            </AccordionSummary>
            <AccordionDetails className={styles.accordionDetails}>
              <Stack spacing={2}>
                <FormControl fullWidth size="small">
                  <InputLabel>Provider</InputLabel>
                  <Select
                    value={formData.provider}
                    label="Provider"
                    disabled={isFormLocked || hasExistingConfig}
                    onChange={(e) => setFormData({ ...formData, provider: e.target.value })}
                  >
                    <MenuItem value="entra">Microsoft Entra</MenuItem>
                  </Select>
                </FormControl>

                <TextField
                  label="Discovery URL"
                  size="small"
                  fullWidth
                  value={formData.discoveryUrl}
                  onChange={handleInputChange('discoveryUrl')}
                  disabled={isFormLocked}
                  error={errors.discoveryUrl}
                  helperText={errors.discoveryUrl ? 'This field is required' : ''}
                />
                <TextField
                  label="Client ID"
                  size="small"
                  fullWidth
                  value={formData.clientId}
                  onChange={handleInputChange('clientId')}
                  disabled={isFormLocked}
                  error={errors.clientId}
                  helperText={errors.clientId ? 'This field is required' : ''}
                />
                <TextField
                  label="Client Secret"
                  type="password"
                  size="small"
                  fullWidth
                  value={formData.clientSecret}
                  onChange={handleInputChange('clientSecret')}
                  disabled={isFormLocked}
                  error={errors.clientSecret}
                  helperText={errors.clientSecret ? 'This field is required' : ''}
                />

                {!isFormLocked && (
                  <Stack direction="row" spacing={2} justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      color="inherit"
                      onClick={handleBack}
                      disabled={loading}
                      className={`${styles.formActionButton}`}
                      sx={{ textTransform: 'none' }}
                    >
                      Back
                    </Button>
                    <Button
                      variant="contained"
                      onClick={handleSaveConfig}
                      disabled={loading}
                      className={`${styles.actionButton} ${styles.formActionButton}`}
                    >
                      {hasExistingConfig ? 'Update' : 'Save'}
                    </Button>
                  </Stack>
                )}
              </Stack>
            </AccordionDetails>
          </Accordion>

          {/* --- PANEL 2: IDP VERIFICATION --- */}
          <AccordionWithTooltip disabled={progressStep < 1} tooltip={panelTooltip}>
            <Accordion
              expanded={expanded === 'panel2'}
              onChange={handleChange('panel2')}
              disabled={(!isSSOEnabled && progressStep >= 2) || progressStep < 1}
              disableGutters
              elevation={0}
              className={styles.accordionRoot}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                <Stack
                  direction="row"
                  alignItems="center"
                  width="100%"
                  justifyContent="space-between"
                >
                  <span className={styles.accordionTitle}>IDP Verification</span>
                  {progressStep >= 2 && (
                    <Tooltip title="IDP Verification is done" arrow placement="top">
                      <CheckCircleIcon
                        color="success"
                        sx={{ fontSize: '18px', marginRight: '10px' }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails className={styles.accordionDetails}>
                <p className={styles.descriptionText}>
                  Please verify the configuration to ensure connectivity with the Identity Provider.
                </p>
                <Button
                  variant="outlined"
                  onClick={handleVerify}
                  disabled={loading || progressStep >= 2}
                  className={styles.outlinedButton}
                  sx={{ width: '200px', textTransform: 'none' }}
                >
                  {progressStep >= 2 ? 'Verified' : 'Verify'}
                </Button>
              </AccordionDetails>
            </Accordion>
          </AccordionWithTooltip>

          {/* --- PANEL 3: SSO ACTIVATION --- */}
          <AccordionWithTooltip disabled={progressStep < 2} tooltip={panelTooltip}>
            <Accordion
              expanded={expanded === 'panel3'}
              onChange={handleChange('panel3')}
              disabled={progressStep < 2}
              disableGutters
              elevation={0}
              className={styles.accordionRoot}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                <Stack
                  direction="row"
                  alignItems="center"
                  width="100%"
                  justifyContent="space-between"
                >
                  <span className={styles.accordionTitle}>SSO Activation</span>
                  {isSSOEnabled && progressStep >= 3 && (
                    <Tooltip title="SSO Activation is done" arrow placement="top">
                      <CheckCircleIcon
                        color="success"
                        sx={{ fontSize: '18px', marginRight: '10px' }}
                      />
                    </Tooltip>
                  )}
                </Stack>
              </AccordionSummary>
              <AccordionDetails className={styles.accordionDetails}>
                <p className={styles.descriptionText}>
                  Enable or Disable SSO login for your organization.
                </p>
                <Stack direction="row" spacing={4} alignItems="center" justifyContent="center">
                  <Button
                    variant={!isSSOEnabled ? 'contained' : 'outlined'}
                    color="error"
                    onClick={() => handleToggleSSO(false)}
                    disabled={!isSSOEnabled || loading}
                    sx={{ textTransform: 'none' }}
                    className={`${styles.formActionButton}`}
                  >
                    Disable
                  </Button>
                  <Button
                    variant={isSSOEnabled ? 'contained' : 'outlined'}
                    onClick={() => handleToggleSSO(true)}
                    disabled={isSSOEnabled || loading}
                    className={
                      isSSOEnabled
                        ? `${styles.actionButton} ${styles.formActionButton}`
                        : `${styles.outlinedButton} ${styles.formActionButton}`
                    }
                  >
                    Enable
                  </Button>
                </Stack>
              </AccordionDetails>
            </Accordion>
          </AccordionWithTooltip>

          {/* --- PANEL 4: SCIM TOKEN --- */}
          <AccordionWithTooltip disabled={progressStep < 3 || !isSSOEnabled} tooltip={panelTooltip}>
            <Accordion
              expanded={expanded === 'panel4'}
              onChange={handleChange('panel4')}
              disabled={progressStep < 3 || !isSSOEnabled}
              disableGutters
              elevation={0}
              className={styles.accordionRoot}
            >
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className={styles.accordionSummary}>
                <span className={styles.accordionTitle}>SCIM Token</span>
              </AccordionSummary>
              <AccordionDetails className={styles.accordionDetails}>
                <div style={{ marginBottom: '16px' }}>
                  <Button
                    variant="contained"
                    onClick={handleGenerateToken}
                    className={styles.actionButton}
                    style={{ marginBottom: '24px' }}
                    sx={{ width: '200px', textTransform: 'none' }}
                  >
                    Generate Token
                  </Button>

                  {scimData.token && (
                    <Stack spacing={2}>
                      <TextField
                        label="SCIM URL"
                        value={scimData.url}
                        variant="filled"
                        fullWidth
                        size="small"
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <Tooltip
                                title={copiedField === 'url' ? 'Copied!' : 'Copy to clipboard'}
                              >
                                <IconButton
                                  onClick={() => handleCopy(scimData.url, 'url')}
                                  size="small"
                                >
                                  <ContentCopyIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                      <TextField
                        label="Bearer Token"
                        value={scimData.token}
                        variant="filled"
                        fullWidth
                        multiline
                        rows={3}
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end" sx={{ alignSelf: 'flex-start', mt: 1 }}>
                              <Tooltip
                                title={copiedField === 'token' ? 'Copied!' : 'Copy to clipboard'}
                              >
                                <IconButton
                                  onClick={() => handleCopy(scimData.token, 'token')}
                                  size="small"
                                >
                                  <ContentCopyIcon sx={{ fontSize: '18px' }} />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>
                          ),
                        }}
                      />
                    </Stack>
                  )}
                </div>
              </AccordionDetails>
            </Accordion>
          </AccordionWithTooltip>

          {/* --- Edit SSO Configuration Button --- */}
          {/* This only appears when configuration exists and is locked */}
          {isFormLocked && (
            <div className={styles.editSection}>
              <p className={styles.editDescription}>
                Need to update your SSO credentials or configuration?
              </p>
              <Button
                variant="text"
                startIcon={<EditIcon sx={{ fontSize: '18px' }} />}
                onClick={handleEditRequest}
                disabled={loading}
                className={styles.editButton}
              >
                Edit SSO Configuration
              </Button>
            </div>
          )}
        </div>
      )}
      {userRole === 'admin' && (
        <div className={styles.ssoContentBody}>
          <Typography variant="body1" sx={{ fontSize: '14px', paddingTop: '120px' }}>
            You must be an Account Admin to access SSO configuration.
          </Typography>
        </div>
      )}
    </div>
  );
};

export default SSOConfiguration;
